// stereo-core: Terzaghi (1965) sampling-bias weighting.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, DEG, dot, unit } from "./vec.js";
import { lineToVec, planeToPole } from "./orient.js";

/** spot = no bias correction; linear = scanline/borehole axis (trend/plunge);
 *  planar = mapped face (dip/dip direction). */
export type Traverse =
  | { kind: "spot" }
  | { kind: "linear"; trend: number; plunge: number }
  | { kind: "planar"; dip: number; dipDir: number };

// [TRZ-01] [TRZ-02] [TRZ-03]
/**
 * Weight = 1 / sin(β), β = angle between the discontinuity and the traverse
 * (linear: β = 90° − angle(pole, line); planar: β = angle between the planes).
 * β is floored at `minBiasDeg` (DIPS default 15°) so weights stay finite.
 */
export function terzaghiWeights(poles: Vec3[], tr: Traverse, minBiasDeg = 15): number[] {
  if (tr.kind === "spot") return poles.map(() => 1);
  const sMin = Math.sin(minBiasDeg * DEG);
  if (tr.kind === "linear") {
    const l = lineToVec(tr.trend, tr.plunge);
    return poles.map((p) => 1 / Math.max(sMin, Math.abs(dot(unit(p), l)))); // sin β = |cos(pole,line)|
  }
  const n = planeToPole(tr.dip, tr.dipDir);
  return poles.map((p) => {
    const c = Math.abs(dot(unit(p), n));
    return 1 / Math.max(sMin, Math.sqrt(Math.max(0, 1 - c * c))); // sin(angle between planes)
  });
}

// [TRZ-04]
/** Weights when rows belong to different traverses (DIPS "Traverse" column).
 *  `traverseOf[i]` indexes into `traverses`; out-of-range means spot (weight 1). */
export function terzaghiWeightsMulti(poles: Vec3[], traverseOf: number[], traverses: Traverse[], minBiasDeg = 15): number[] {
  const out = new Array<number>(poles.length).fill(1);
  traverses.forEach((tr, t) => {
    const idx: number[] = [];
    traverseOf.forEach((k, i) => { if (k === t) idx.push(i); });
    if (!idx.length) return;
    const w = terzaghiWeights(idx.map((i) => poles[i]), tr, minBiasDeg);
    idx.forEach((i, j) => (out[i] = w[j]));
  });
  return out;
}
